// Client-side "Simulating…" signal.
//
// The simulation engine (tbcalc) runs in-process in the single-threaded
// R/Shiny session. While simulate_reform() computes (~1s+ with PIT), R is
// blocked and cannot push a "working" message — so a server-driven spinner
// can never paint. Instead we bracket the work entirely on the client:
//
//   show:    the instant the Simulate button is clicked (this fires in the
//            browser before R even receives the click).
//   dismiss: when the engine output lands back over the custom-message
//            channel (state/shiny_bridge.ts calls setSimulating(false)).
//
// This precisely brackets one Simulate. A generic `shiny:busy` listener
// would also fire on every slider tick (each sends input to the server),
// flickering the overlay; keying off the button click avoids that.

import { useEffect } from "react";
import { useResultsStore } from "./results_store";

// Safety net: if the engine errors and no engine_output ever arrives, never
// leave the overlay stuck. Generous — a real reform is ~1-2s.
const SAFETY_TIMEOUT_MS = 20_000;

const SIMULATE_BUTTON_SELECTOR = '[data-testid="simulate-button"]';

/** Wire the Simulate-button click to the `isSimulating` flag. Call once near
 *  the island root. Dismiss is handled by useShinyEngineBridge. */
export function useSimulatingSignal(): void {
  const setSimulating = useResultsStore((s) => s.setSimulating);
  const isSimulating = useResultsStore((s) => s.isSimulating);

  // Show on click. Delegated on document so it survives Shiny re-rendering
  // the button. The button is disabled while simulating, so a disabled
  // button emits no click — no double-trigger.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(SIMULATE_BUTTON_SELECTOR)) {
        setSimulating(true);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [setSimulating]);

  // Mirror the flag onto the Shiny-rendered Simulate button, which lives
  // outside this React tree (the inputs column). Class drives the busy
  // label/spinner; `disabled` blocks a second submit mid-compute.
  useEffect(() => {
    const button = document.querySelector<HTMLButtonElement>(
      SIMULATE_BUTTON_SELECTOR,
    );
    if (!button) return;
    button.classList.toggle("is-simulating", isSimulating);
    button.disabled = isSimulating;
  }, [isSimulating]);

  // Safety timeout, scoped to the active spell. Cleanup clears it the moment
  // isSimulating flips back to false (the engine_output dismiss), so a stale
  // timer can never hide a *later* in-flight simulate.
  useEffect(() => {
    if (!isSimulating) return;
    const timer = window.setTimeout(
      () => setSimulating(false),
      SAFETY_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isSimulating, setSimulating]);
}
